import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Table, Spinner, Alert, InputGroup } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from './ToastNotification';

const formatPrice = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VLASTNI = '__vlastni__';

/**
 * Dobropis (opravný daňový doklad) k vystavené faktuře — i částečný.
 *
 * Tohle je ten běžný případ: inzerát vyšel špatně, zákazník dostane slevu
 * 2 000 Kč. Z dobropisů v datech jich 914 z 941 vypadá takhle — řádek s důvodem
 * a vlastní částkou, ne dobropis celé položky.
 *
 * Částky se zadávají **bez DPH** a kladně; doklad umí jen snižovat, znaménko
 * obrací backend. Přes zbytek položky (po dřívějších dobropisech) to nepustí.
 */
function InvoiceCreditNoteModal({ show, invoiceId, onHide, openTab }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [radky, setRadky] = useState({});
  // Vlastní položky — jen název a částka, na položku faktury se neváží
  const [vlastniPolozky, setVlastniPolozky] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !invoiceId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getInvoiceCreditNoteInfo(invoiceId);
        if (cancelled) return;
        const data = res.data || null;
        setInfo(data);
        // Předvyplněný je nejčastější důvod z historie (první v seznamu) a prázdná
        // částka — uživatel vyplní jen u položek, kterých se oprava týká.
        const vychoziDuvod = data?.reasons?.[0]?.id ?? '';
        setRadky(Object.fromEntries(
          (data?.items || []).map((i) => [i.id, { castka: '', duvod: vychoziDuvod, vlastni: '', procenta: false }]),
        ));
        setVlastniPolozky([]);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Nepodařilo se načíst podklad pro dobropis.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, invoiceId]);

  const uprav = (id, zmena) => setRadky((prev) => ({ ...prev, [id]: { ...prev[id], ...zmena } }));

  const pridejVlastni = () => setVlastniPolozky((prev) => [...prev, { key: Date.now() + Math.random(), nazev: '', castka: '' }]);
  const upravVlastni = (key, zmena) => setVlastniPolozky((prev) => prev.map((r) => (r.key === key ? { ...r, ...zmena } : r)));
  const smazVlastni = (key) => setVlastniPolozky((prev) => prev.filter((r) => r.key !== key));

  const castkaVlastni = (radek) => {
    const zadano = parseFloat(String(radek.castka).replace(',', '.'));
    return zadano > 0 ? Math.round(zadano * 100) / 100 : 0;
  };

  const mena = info?.invoice?.currency || 'CZK';

  /** Částka řádku v korunách — z procent se počítá ze zbytku položky. */
  const castkaRadku = (polozka) => {
    const radek = radky[polozka.id];
    if (!radek) return 0;
    const zadano = parseFloat(String(radek.castka).replace(',', '.'));
    if (!(zadano > 0)) return 0;
    const castka = radek.procenta ? (polozka.remaining * zadano) / 100 : zadano;
    return Math.round(castka * 100) / 100;
  };

  const chyby = useMemo(() => {
    if (!info) return [];
    const seznam = [];
    info.items.forEach((polozka) => {
      const castka = castkaRadku(polozka);
      if (castka <= 0) return;
      if (castka > polozka.remaining + 0.001) {
        seznam.push(`${polozka.number}: zbývá dobropisovat ${formatPrice(polozka.remaining)} ${mena}`);
      }
      const radek = radky[polozka.id];
      if (radek.duvod === VLASTNI && !radek.vlastni.trim()) {
        seznam.push(`${polozka.number}: doplň vlastní důvod`);
      }
    });

    vlastniPolozky.forEach((radek, i) => {
      const castka = castkaVlastni(radek);
      if (castka > 0 && !radek.nazev.trim()) {
        seznam.push(`Vlastní položka ${i + 1}: doplň název`);
      }
      if (castka <= 0 && radek.nazev.trim()) {
        seznam.push(`${radek.nazev.trim()}: doplň částku`);
      }
    });

    // Dobropisovat víc, než kolik na faktuře zbývá, nejde — vlastní řádky se
    // na položku neváží, takže je hlídá jen tenhle celkový strop.
    const soucet = (info.items || []).reduce((s, p) => s + castkaRadku(p), 0)
      + vlastniPolozky.reduce((s, r) => s + (r.nazev.trim() ? castkaVlastni(r) : 0), 0);
    if (soucet > info.remainingTotal + 0.001) {
      seznam.push(`na faktuře zbývá k dobropisování ${formatPrice(info.remainingTotal)} ${mena}`);
    }

    return seznam;
  }, [info, radky, vlastniPolozky]); // eslint-disable-line react-hooks/exhaustive-deps

  const celkem = useMemo(
    () => (info?.items || []).reduce((soucet, polozka) => soucet + castkaRadku(polozka), 0)
      + vlastniPolozky.reduce((soucet, radek) => soucet + (radek.nazev.trim() ? castkaVlastni(radek) : 0), 0),
    [info, radky, vlastniPolozky], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const lzeVystavit = celkem > 0 && chyby.length === 0;

  const handleCreate = async () => {
    if (creating || !lzeVystavit) return;
    setCreating(true);
    setError('');
    try {
      const items = info.items
        .map((polozka) => {
          const castka = castkaRadku(polozka);
          if (castka <= 0) return null;
          const radek = radky[polozka.id];
          const vlastni = radek.duvod === VLASTNI;
          const duvod = info.reasons.find((r) => String(r.id) === String(radek.duvod));
          return {
            invoice_item_id: polozka.id,
            amount: castka,
            discount_type_id: vlastni ? null : radek.duvod,
            reason: vlastni ? radek.vlastni.trim() : (duvod?.label || ''),
          };
        })
        .filter(Boolean);

      vlastniPolozky.forEach((radek) => {
        const castka = castkaVlastni(radek);
        if (castka > 0 && radek.nazev.trim()) {
          items.push({ invoice_item_id: null, amount: castka, name: radek.nazev.trim() });
        }
      });

      const res = await api.createInvoiceCreditNote(invoiceId, { items });
      if (res.status === 'ok' && res.data) {
        showToast(`Dobropis ${res.data.number} vystaven.`, 'success');
        window.dispatchEvent(new CustomEvent('invoices:refresh'));
        onHide();
        if (openTab) {
          openTab(`invoice-${res.data.id}`, 'Dobropis', `/invoices/${res.data.id}`, 'InvoiceDetail', { invoiceId: res.data.id });
        }
      } else {
        setError(res.message || 'Dobropis se nepodařilo vystavit.');
      }
    } catch (e) {
      setError(e.message || 'Dobropis se nepodařilo vystavit.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !creating && onHide()} centered size="lg">
      <Modal.Header closeButton={!creating}>
        <Modal.Title><i className="bi bi-arrow-counterclockwise me-2" />Vystavit dobropis</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Načítání…</div>
        ) : info ? (
          <>
            <div className="small text-muted mb-3">
              Oprava k faktuře <strong>{info.invoice.number}</strong> · {formatPrice(info.invoice.net_net_price)} {mena} bez DPH
              {info.creditNotes.length > 0 && (
                <> · už dobropisováno: {info.creditNotes.map((d) => `${d.number} (${formatPrice(d.net_net_price)})`).join(', ')}</>
              )}
            </div>

            <Table size="sm" className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Položka</th>
                  <th className="text-end text-nowrap">Zbývá</th>
                  <th style={{ width: '38%' }}>Důvod</th>
                  <th className="text-end" style={{ width: '9rem' }}>Dobropisovat (bez DPH)</th>
                </tr>
              </thead>
              <tbody>
                {info.items.map((polozka) => {
                  const radek = radky[polozka.id] || {};
                  const castka = castkaRadku(polozka);
                  const vycerpano = polozka.remaining <= 0;
                  return (
                    <tr key={polozka.id} className={vycerpano ? 'text-muted' : undefined}>
                      <td>
                        <div className="small">{polozka.name}</div>
                        <div className="small text-muted">{polozka.number} · DPH {polozka.vat_rate} %</div>
                      </td>
                      <td className="text-end text-nowrap">
                        {formatPrice(polozka.remaining)} {mena}
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={radek.duvod ?? ''}
                          disabled={vycerpano}
                          onChange={(e) => uprav(polozka.id, { duvod: e.target.value })}
                        >
                          {(info.reasons || []).map((r) => (
                            <option key={r.id} value={r.id}>{r.label}</option>
                          ))}
                          <option value={VLASTNI}>Vlastní důvod…</option>
                        </Form.Select>
                        {radek.duvod === VLASTNI && (
                          <Form.Control
                            size="sm"
                            className="mt-1"
                            placeholder="např. Nevyšel inzerát v termínu"
                            value={radek.vlastni || ''}
                            onChange={(e) => uprav(polozka.id, { vlastni: e.target.value })}
                          />
                        )}
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Form.Control
                            className="text-end"
                            value={radek.castka || ''}
                            disabled={vycerpano}
                            placeholder="0"
                            onChange={(e) => uprav(polozka.id, { castka: e.target.value })}
                          />
                          <Button
                            variant={radek.procenta ? 'secondary' : 'outline-secondary'}
                            disabled={vycerpano}
                            title={radek.procenta ? 'Zadáváš procenta ze zbytku položky' : 'Zadáváš částku v ' + mena}
                            onClick={() => uprav(polozka.id, { procenta: !radek.procenta })}
                          >
                            {radek.procenta ? '%' : mena}
                          </Button>
                        </InputGroup>
                        {radek.procenta && castka > 0 && (
                          <div className="small text-muted text-end mt-1">= {formatPrice(castka)} {mena}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="small fw-bold">Vlastní položky</div>
              <Button variant="outline-secondary" size="sm" onClick={pridejVlastni}>
                <i className="bi bi-plus-lg me-1" />Přidat řádek
              </Button>
            </div>
            {vlastniPolozky.length > 0 && (
              <Table size="sm" className="align-middle mb-3">
                <tbody>
                  {vlastniPolozky.map((radek) => (
                    <tr key={radek.key}>
                      <td>
                        <Form.Control
                          size="sm"
                          placeholder="Název položky, např. Kompenzace za nevydání"
                          value={radek.nazev}
                          onChange={(e) => upravVlastni(radek.key, { nazev: e.target.value })}
                        />
                      </td>
                      <td style={{ width: '9rem' }}>
                        <InputGroup size="sm">
                          <Form.Control
                            className="text-end"
                            placeholder="0"
                            value={radek.castka}
                            onChange={(e) => upravVlastni(radek.key, { castka: e.target.value })}
                          />
                          <InputGroup.Text>{mena}</InputGroup.Text>
                        </InputGroup>
                      </td>
                      <td style={{ width: 36 }}>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          title="Odebrat řádek"
                          onClick={() => smazVlastni(radek.key)}
                        >
                          <i className="bi bi-x-lg" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {chyby.length > 0 && (
              <Alert variant="warning" className="py-2 small mb-0">
                <i className="bi bi-exclamation-triangle me-1" />
                {chyby.join(' · ')}
              </Alert>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <div className="me-auto small">
          Dobropis na <strong>{formatPrice(celkem)} {mena}</strong> bez DPH
        </div>
        <Button variant="outline-secondary" onClick={onHide} disabled={creating}>Zrušit</Button>
        <Button variant="primary" onClick={handleCreate} disabled={loading || creating || !lzeVystavit}>
          {creating
            ? <><Spinner animation="border" size="sm" className="me-1" />Vystavuji…</>
            : <><i className="bi bi-check-lg me-1" />Vystavit dobropis</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceCreditNoteModal;
